import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { Context } from 'src/app/shared/models/context';
import { Leave, PrivilegeType } from 'src/app/shared/models/domain';
import { AppearanceService } from 'src/app/shared/services/appearance.service';
import { AuthService } from 'src/app/shared/services/auth.service';
import { CommonService } from 'src/app/shared/services/common.service';
import * as XLSX from 'xlsx';

@Component({
  selector: 'app-vacay',
  templateUrl: './vacay.component.html',
  styleUrls: ['./vacay.component.scss']
})
export class VacayComponent implements OnInit {
  public vacays: Leave[] = [];
  public context: Context;
  public fromDate: string;
  public toDate: string;
  public status: string = 'APPLIED';
  public isLoading: boolean = true;
  public privilege: PrivilegeType;
  public statuses: string[] = [];
  public showAllLeaves: boolean;
  public useRegularReport: boolean = false;

  constructor(
    private commonService: CommonService,
    private datePipe: DatePipe,
    private authService: AuthService,
    private appearanceService: AppearanceService
  ) { }

  ngOnInit(): void {
    this.fromDate = this.datePipe.transform(this.commonService.adjustDateByDays(-30), 'yyyy-MM-dd');
    this.toDate = this.datePipe.transform(this.commonService.adjustDateByDays(30), 'yyyy-MM-dd');
    this.context = this.authService.getContextData();
    if (this.context && this.context?.companyId) {
      this.getVacays();
    }
    this.getVacayStatuses();
  }

  private getVacays(clear: boolean = true) {
    if (this.context?.employeeId) {
      if (clear) {
        this.isLoading = true;
        this.vacays = [];
      }
      if (!this.fromDate || !this.toDate) {
        this.commonService.setToastr(500, "Both \"From Date\" and \"To Date\" are required to proceed.");
        return;
      }
      const fromDate = this.datePipe.transform(this.fromDate, 'dd-MM-yyyy');
      const toDate = this.datePipe.transform(this.toDate, 'dd-MM-yyyy');

      const apiCall = this.useRegularReport
        ? this.appearanceService.getRegularReports(this.context.employeeId, fromDate, toDate, this.status, this.showAllLeaves)
        : this.appearanceService.getLeaves(this.context.employeeId, fromDate, toDate, this.status, this.showAllLeaves);

      apiCall.subscribe({
        next: (vacays: Leave[]) => {
          this.vacays = this.showAllLeaves ? vacays : vacays.filter(vacay => vacay.appliedBy.id === this.context.employeeId);
          this.vacays = vacays.sort((a, b) => a.id - b.id);
          this.isLoading = false;
        },
        error: (error: HttpErrorResponse) => {
          this.isLoading = false;
          this.commonService.setToastr(500, error?.error?.name || error.error);
        }
      });
    } else {
      this.isLoading = false;
    }
  }

  private getVacayStatuses() {
    this.appearanceService.getStatus().subscribe((statuses) => {
      this.statuses = statuses.filter(s => s !== 'COMPLETED');
    });
  }

  public onStatusChange() {
    this.getVacays(true);
  }

  public downloadExcel() {
    if (!this.context?.employeeId || !this.fromDate || !this.toDate) {
      this.commonService.setToastr(500, "Please provide valid date range and employee context.");
      return;
    }

    const fromDate = this.datePipe.transform(this.fromDate, 'dd-MM-yyyy');
    const toDate = this.datePipe.transform(this.toDate, 'dd-MM-yyyy');

    const apiCall = this.useRegularReport
      ? this.appearanceService.getRegularReports(this.context.employeeId, fromDate, toDate, this.status, this.showAllLeaves)
      : this.appearanceService.getLeaves(this.context.employeeId, fromDate, toDate, this.status, this.showAllLeaves);

    this.isLoading = true;
    apiCall.subscribe({
      next: (data: Leave[]) => {
        this.isLoading = false;
        const filteredData = this.showAllLeaves ? data : data.filter(vacay => vacay.appliedBy.id === this.context.employeeId);
        const worksheetData = filteredData.map((vacay, index) => ({
          '#': index + 1,
          Employee: vacay.status === null ? vacay.appliedFor?.name : vacay.appliedFor?.name || '-',
          Type: vacay.status === null ? 'ABSENCE' : vacay.leaveType || '-',
          From: this.datePipe.transform(vacay.appliedFrom, 'MMM dd, yyyy') || '-',
          To: this.datePipe.transform(vacay.appliedTo, 'MMM dd, yyyy') || '-',
          Duration: vacay.partial ? 'Half Day' : 'Full Day',
          Reason: vacay.reason || '-',
          Comment: vacay.comments || vacay.remarks || '-',
          Count: this.useRegularReport ? (vacay.regularCount || '-') : (vacay.leaveCount || '-'), // Use the correct count field
          Status: vacay.status || 'APPROVED'
        }));

        const worksheet = XLSX.utils.json_to_sheet(worksheetData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Vacays');
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        this.saveAsExcelFile(excelBuffer, 'vacays_data');
      },
      error: (error: HttpErrorResponse) => {
        this.isLoading = false;
        this.commonService.setToastr(500, error?.error?.name || error.error);
      }
    });
  }

  private saveAsExcelFile(buffer: any, fileName: string) {
    const data: Blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });
    const url = window.URL.createObjectURL(data);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${fileName}_${new Date().toISOString().split('T')[0]}.xlsx`;
    link.click();
    window.URL.revokeObjectURL(url);
  }
}